/**
 * The customer and partner testimonials Steve supplied on 16 September 2026,
 * transcribed verbatim.
 *
 * They live here rather than inline in each page because several of them are
 * used on more than one page, and the words are the customer's: a page that
 * paraphrased or trimmed one to fit its layout would be misquoting them. One
 * definition, one place to correct it.
 *
 * Attribution is the sector, not the company, which is how Steve supplied them.
 * When a named customer agrees to be quoted, change `attribution` here and every
 * page that uses the quote follows.
 */

export const MANUFACTURING_EASY_TO_ADMINISTER = {
  quote:
    'IMPROVE is incredibly user-friendly and very easy to administer, while our employees genuinely enjoy completing the courses.',
  attribution: 'Manufacturing Sector Customer',
};

export const CHARITY_ADMIN_TIME_SAVED = {
  quote:
    'IMPROVE saves me around 3–4 hours of administration time each month, while giving us the flexibility to create learning pathways tailored to different departments.',
  attribution: 'Charity Sector Customer',
};

export const MANUFACTURING_INDUCTION = {
  quote:
    'IMPROVE has become an important part of our induction programme, helping us deliver consistent training to every new starter.',
  attribution: 'Manufacturing Sector Customer',
};

/**
 * Already on /solutions/onboarding before this brief, from a named customer.
 * Kept, and now sits beside Steve's induction quote rather than instead of it.
 */
export const ADVOCACY_FOCUS_ONBOARDING = {
  quote:
    'New staff have been in ‘awe’ of our onboarding process which is all centrally delivered via IMPROVE.',
  attribution: 'Advocacy Focus',
};

export const MANUFACTURING_COURSE_RANGE = {
  quote:
    'IMPROVE gives us a great range of courses for induction, but also allows us to provide additional learning to support the ongoing training and development of our people.',
  attribution: 'Manufacturing Sector Customer',
};

export const CHARITY_COURSE_VARIETY = {
  quote:
    'The increased number and variety of courses available has also been a real positive, helping to improve engagement with training across the charity.',
  attribution: 'Charity Sector Customer',
};

export const MANUFACTURING_OWN_CONTENT = {
  quote:
    "We particularly value the ability to upload our own learning materials alongside IMPROVE's courses.",
  attribution: 'Manufacturing Sector Customer',
};

export const CHARITY_REPORTING = {
  quote:
    "The reporting functionality gives managers greater visibility of their teams' training and allows them to access the relevant reports.",
  attribution: 'Charity Sector Customer',
};

/**
 * The short form of CHARITY_ADMIN_TIME_SAVED. Steve supplied both: the long one
 * carries the homepage, the short one sits under the reporting quote where the
 * pathways half would repeat what that section already says.
 */
export const CHARITY_ADMIN_TIME_SAVED_SHORT = {
  quote: 'IMPROVE saves me around 3–4 hours of administration time each month.',
  attribution: 'Charity Sector Customer',
};

/**
 * Steve asked for this one to read as a PARTNER testimonial rather than a
 * customer one, hence the eyebrow and the second attribution line.
 */
export const HRCENTRAL_PARTNER = {
  eyebrow: 'Partner Testimonial',
  quote:
    'The system is like no other in the market. HRCentral Limited and our clients are continually impressed by the user-friendly, flexible and limitless training opportunities IMPROVE can offer.',
  attribution: 'HRCentral Limited',
  role: 'IMPROVE Partner',
};

/**
 * Supplied in the same brief but NOT placed yet: Steve headed these "Charity
 * Sector Page" and the site has no sector pages, only "case study coming soon"
 * cards on /customer-stories. Held deliberately, pending his answer on whether
 * to build a charity page or fold them into Customer Stories. Exported so the
 * wording is already transcribed when he decides.
 */
export const CHARITY_VALUE_FOR_MONEY = {
  quote:
    'Overall, we feel the platform offers excellent value for money compared with other providers we have considered, making it a great option for charities looking for a user-friendly and cost-effective training solution.',
  attribution: 'Charity Sector Customer',
};

export const CHARITY_LEARNING_PATHWAYS = {
  quote:
    'The ability to create individual and team learning pathways has significantly reduced confusion around which courses users need to complete, while also making the platform much more user-friendly.',
  attribution: 'Charity Sector Customer',
};

/**
 * Trilogy Active, 16 September 2026, the first customer quoted by name.
 * Attribution exactly as Steve supplied it.
 */
const TRILOGY = 'Head of People and Development';
const TRILOGY_ROLE = 'Trilogy Active, Not for Profit Sector';

export const TRILOGY_EASY = {
  quote: "It's easy for us to manage and easy for our people to use, which is what you want from a learning platform.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_SIMPLER = {
  quote:
    "It's made training much simpler for us. We've got consistency across all our sites, it's easier to keep track of mandatory training and it's helped make onboarding much more straightforward.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_ONBOARDING = {
  quote: "It's helped make onboarding much more straightforward.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_BITE_SIZED = {
  quote:
    "The modules are bite-sized, and some are short microlearning pieces, so people aren't having to sit through long training sessions.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_CPD = {
  quote: 'It also gives us the opportunity to support professional qualifications and CPD.',
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_COMPLIANCE = {
  quote: "We've got consistency across all our sites, and it's easier to keep track of mandatory training.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_VALUE = {
  quote:
    "It has also saved us money compared with some of the training solutions we've used previously, while helping us develop a better-skilled team.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_SUPPORT = {
  quote:
    "The IT support team are quick and helpful, which makes a difference when you're managing learning across a whole organisation.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_RENEWED = {
  quote:
    "We've renewed because it works well for us. It's flexible, easy to use and engaging for our people. It works alongside our existing mandatory training, but also gives us the opportunity to support professional qualifications and CPD.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

/** The Customer Stories card: the facts Steve supplied with the quote. */
export const TRILOGY_STORY = {
  company: 'Trilogy Active',
  sector: 'Not-for-Profit / Health, Wellbeing & Leisure',
  facts: '12 sites | 250 people',
  ...TRILOGY_SIMPLER,
};
